/**
 * Webhook Service (Frontend)
 * Handles API calls for webhook management
 */

import { supabase } from '../utils/supabaseClient';
import {
  Webhook,
  CreateWebhookRequest,
  UpdateWebhookRequest,
  WebhookTestResult,
} from '../types/webhook';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

/**
 * Get authorization header with Supabase JWT
 */
const getAuthHeaders = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  
  if (!session?.access_token) {
    throw new Error('No active session');
  }

  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${session.access_token}`,
  };
};

/**
 * Create a new webhook
 */
export const createWebhook = async (
  data: CreateWebhookRequest
): Promise<Webhook> => {
  try {
    const headers = await getAuthHeaders();

    const response = await fetch(`${API_BASE_URL}/api/developers/webhooks`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create webhook');
    }

    const webhook = await response.json();
    
    // Convert date strings to Date objects
    return {
      ...webhook,
      createdAt: new Date(webhook.createdAt),
      updatedAt: new Date(webhook.updatedAt),
    };
  } catch (error) {
    console.error('[WebhookService] Error creating webhook:', error);
    throw error;
  }
};

/**
 * Get all webhooks for the current tenant
 */
export const getWebhooks = async (): Promise<Webhook[]> => {
  try {
    const headers = await getAuthHeaders();

    const response = await fetch(`${API_BASE_URL}/api/developers/webhooks`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to fetch webhooks');
    }

    const data = await response.json();
    
    // Convert date strings to Date objects
    return data.map((webhook: any) => ({
      ...webhook,
      createdAt: new Date(webhook.createdAt),
      updatedAt: new Date(webhook.updatedAt),
    }));
  } catch (error) {
    console.error('[WebhookService] Error fetching webhooks:', error);
    throw error;
  }
};

/**
 * Get a single webhook
 */
export const getWebhook = async (id: string): Promise<Webhook> => {
  try {
    const headers = await getAuthHeaders();

    const response = await fetch(`${API_BASE_URL}/api/developers/webhooks/${id}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to fetch webhook');
    }

    const webhook = await response.json();
    
    // Convert date strings to Date objects
    return {
      ...webhook,
      createdAt: new Date(webhook.createdAt),
      updatedAt: new Date(webhook.updatedAt),
    };
  } catch (error) {
    console.error('[WebhookService] Error fetching webhook:', error);
    throw error;
  }
};

/**
 * Update a webhook
 */
export const updateWebhook = async (
  id: string,
  data: UpdateWebhookRequest
): Promise<Webhook> => {
  try {
    const headers = await getAuthHeaders();

    const response = await fetch(`${API_BASE_URL}/api/developers/webhooks/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update webhook');
    }

    const webhook = await response.json();
    
    // Convert date strings to Date objects
    return {
      ...webhook,
      createdAt: new Date(webhook.createdAt),
      updatedAt: new Date(webhook.updatedAt),
    };
  } catch (error) {
    console.error('[WebhookService] Error updating webhook:', error);
    throw error;
  }
};

/**
 * Delete a webhook
 */
export const deleteWebhook = async (id: string): Promise<void> => {
  try {
    const headers = await getAuthHeaders();

    const response = await fetch(`${API_BASE_URL}/api/developers/webhooks/${id}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to delete webhook');
    }
  } catch (error) {
    console.error('[WebhookService] Error deleting webhook:', error);
    throw error;
  }
};

/**
 * Test a webhook
 */
export const testWebhook = async (id: string): Promise<WebhookTestResult> => {
  try {
    const headers = await getAuthHeaders();

    const response = await fetch(
      `${API_BASE_URL}/api/developers/webhooks/${id}/test`,
      {
        method: 'POST',
        headers,
      }
    );

    if (!response.ok) {
      const error = await response.json();
      return {
        success: false,
        error: error.message || 'Failed to test webhook',
      };
    }

    const result = await response.json();
    return result.result;
  } catch (error) {
    console.error('[WebhookService] Error testing webhook:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};
